import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';
import {
  getStoredLocale,
  LOCALE_STORAGE_KEY,
  normalizeLocaleCode,
  persistLocale,
  setDocumentLanguageTag,
  SUPPORTED_LOCALES
} from './locale';
import { defaultNamespace, namespaces, resources } from './resources';
import { brandResources } from './brand';
import { accountAccessResources } from './accountAccess';
import { appearanceResources } from './appearance';
import { publicSiteResources } from './publicSite';
import { assessmentExperienceResources } from './assessmentExperience';
import { publicExperienceResources } from './publicExperience';
import { applyLocaleAwareIntlDefaults } from './runtimeIntl';

const fallbackLocale = 'en';
// Hungarian-first: the placement assessment defaults to Hungarian unless the
// user explicitly picked another language before (English is the i18n fallback).
const initialLocale = getStoredLocale() || 'hu';

setDocumentLanguageTag(initialLocale);
persistLocale(initialLocale);
applyLocaleAwareIntlDefaults();

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: Object.fromEntries(Object.entries(resources).map(([locale, value]) => [locale, {
      ...value,
      brand: brandResources[locale as keyof typeof brandResources],
      accountAccess: accountAccessResources[locale as keyof typeof accountAccessResources],
      appearance: appearanceResources[locale as keyof typeof appearanceResources],
      publicSite: publicSiteResources[locale as keyof typeof publicSiteResources],
      assessmentExperience: assessmentExperienceResources[locale as keyof typeof assessmentExperienceResources],
      publicExperience: publicExperienceResources[locale as keyof typeof publicExperienceResources]
    }])),
    lng: initialLocale,
    fallbackLng: fallbackLocale,
    supportedLngs: [...SUPPORTED_LOCALES],
    ns: [...namespaces, 'brand', 'accountAccess', 'appearance', 'publicSite', 'assessmentExperience', 'publicExperience'],
    defaultNS: defaultNamespace,
    interpolation: {
      escapeValue: false
    },
    returnNull: false,
    load: 'languageOnly',
    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      lookupLocalStorage: LOCALE_STORAGE_KEY,
      caches: ['localStorage']
    }
  });

i18n.on('languageChanged', (language) => {
  const normalized = normalizeLocaleCode(language);

  if (normalized !== language) {
    void i18n.changeLanguage(normalized);
    return;
  }

  persistLocale(normalized);
  setDocumentLanguageTag(normalized);
});

export default i18n;
